import React, { forwardRef, useRef, useImperativeHandle, useState } from "react";
import { TextInput, Dropdown, TextArea, ActionButton } from "colbi_web_ui/lib/components/inputs";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";

import styles from "./FreshDeskForm.module.sass";
import Dropzone from "colbi_web_ui/lib/components/Dropzone/Dropzone";
import ReCaptcha from "react-google-recaptcha";

const FreshDeskForm = (props, ref) => {
    const { data, freshDeskFormConfigs, status, onChange, onSave, errors } = props;
    const formRef = useRef();

    const { email, subject, ticketContext, name, type, priority } = data || {};

    const modalErrors = errors && errors.length ? errors.map(({ errors }) => errors[0]) : [];

    const { types, priorities, recaptchaKeys } = freshDeskFormConfigs || {};

    const { siteKey } = recaptchaKeys || {};

    const { i18n, params } = useGlobalValue();

    const [files, setFiles] = useState([]);

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    return status !== "loading" ? (
        <div className={styles["container"]}>
            <form
                ref={formRef}
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (formRef.current.checkValidity()) {
                        data["uploadedFiles"] = files;
                        data["reCaptcha"] = data["reCaptcha"] ? true : false;
                        data["status"] = 2;
                        onSave && onSave();
                    } else {
                        formRef.current.reportValidity();
                    }
                }}
                className={styles["form-ticket"]}
            >
                <div className={styles["group-content"]}>
                    <h3 className={styles["form-title"]}>{i18n`Submit your ticket`}</h3>
                    <div className={styles["flex"]}>
                        <TextInput
                            className={styles["input"]}
                            name={i18n`Username`}
                            value={name || ""}
                            disabled
                            required
                            placeholder={i18n`Enter your name`}
                        />
                        {modalErrorBuilder(modalErrors, "username", i18n)
                            ? modalErrorBuilder(modalErrors, "username", i18n)
                            : null}
                    </div>
                    <div className={styles["flex"]}>
                        <TextInput
                            className={styles["input"]}
                            name={i18n`Email`}
                            value={email || ""}
                            placeholder={i18n`Enter your email`}
                            disabled
                            type={"email"}
                            required
                        />
                        {modalErrorBuilder(modalErrors, "email", i18n)
                            ? modalErrorBuilder(modalErrors, "email", i18n)
                            : null}
                    </div>
                    <div className={styles["flex"]}>
                        <TextInput
                            className={styles["input"]}
                            name={i18n`Subject`}
                            value={subject || ""}
                            placeholder={i18n`Enter a subject`}
                            disabled
                            required
                            onChange={(e) => {
                                handleChange(e, "subject", e.target.value);
                            }}
                        />
                        {modalErrorBuilder(modalErrors, "subject", i18n)
                            ? modalErrorBuilder(modalErrors, "subject", i18n)
                            : null}
                    </div>
                    <div className={styles["input-field"]}>
                        <label style={{ display: "block" }}>
                            <h5 className={styles["label"]}>{i18n`Type`}</h5>
                            <Dropdown
                                value={
                                    type && types
                                        ? types.find(({ name }) => name === type) || types[0]
                                        : null
                                }
                                options={types}
                                placeholder={i18n`-- Select a type --`}
                                className={styles["dropdown"]}
                                required
                                onChange={(e) => {
                                    handleChange(e, "type", e.target.value.name);
                                }}
                            />
                        </label>
                        {modalErrorBuilder(modalErrors, "type", i18n)
                            ? modalErrorBuilder(modalErrors, "type", i18n)
                            : null}
                    </div>
                    <div className={styles["input-field"]}>
                        <h5 className={styles["label"]}>{i18n`Priority`}</h5>
                        <Dropdown
                            value={
                                priority && priorities
                                    ? priorities.find(({ id }) => id === priority) || priorities[0]
                                    : null
                            }
                            options={priorities}
                            className={styles["dropdown"]}
                            placeholder={i18n`-- Select a priority --`}
                            required
                            onChange={(e) => {
                                handleChange(e, "priority", parseInt(e.target.value.id));
                            }}
                        />
                        {modalErrorBuilder(modalErrors, "priority", i18n)
                            ? modalErrorBuilder(modalErrors, "priority", i18n)
                            : null}
                    </div>
                    <div className={styles["input-field"]}>
                        <TextArea
                            className={styles["input"]}
                            style={{ resize: "none" }}
                            placeholder={i18n`Enter a description`}
                            name={i18n`Description`}
                            required
                            onChange={(e) => {
                                handleChange(e, "description", e.target.value);
                            }}
                        />
                        {modalErrorBuilder(modalErrors, "description", i18n)
                            ? modalErrorBuilder(modalErrors, "description", i18n)
                            : null}
                    </div>
                    <div className={styles["input-field"]}>
                        <TextArea
                            className={styles["input"]}
                            style={{ resize: "none" }}
                            value={ticketContext || ""}
                            name={i18n`Ticket Context`}
                            required
                            onChange={(e) => {
                                handleChange(e, "ticketContext", e.target.value);
                            }}
                        />
                        {modalErrorBuilder(modalErrors, "ticketContext", i18n)
                            ? modalErrorBuilder(modalErrors, "ticketContext", i18n)
                            : null}
                    </div>
                    <div>
                        <Dropzone setFiles={setFiles} files={files} />
                        {modalErrorBuilder(modalErrors, "fileSize", i18n)
                            ? modalErrorBuilder(modalErrors, "fileSize", i18n)
                            : null}
                    </div>
                    <div>
                        <ReCaptcha
                            size="normal"
                            render="explicit"
                            sitekey={`${siteKey}`}
                            onChange={(e) => {
                                handleChange(e, "reCaptcha", e ? true : false);
                            }}
                            hl={params.locale}
                            className={styles["recaptcha-container"]}
                        />
                        {modalErrorBuilder(modalErrors, "reCaptcha", i18n)
                            ? modalErrorBuilder(modalErrors, "reCaptcha", i18n)
                            : null}
                    </div>
                    <div className={styles["submit-button-container"]}>
                        {errors && errors.length ? (
                            <div
                                className={`${styles["error"]}  ${styles["generic-error"]}`}
                                key={"freshdesk-form-generic-error"}
                            >
                                <small>{i18n`freshdesk-form-error`}</small>
                            </div>
                        ) : null}
                        <ActionButton
                            style={{ display: "flex", justifyContent: "center" }}
                            primary
                            key="action-save"
                            type="submit"
                        >
                            <small>{i18n`Submit`}</small>
                        </ActionButton>
                    </div>
                </div>
            </form>
        </div>
    ) : null;
};

const FreshDeskFormRef = forwardRef(FreshDeskForm);

FreshDeskFormRef.queries = () => {
    return [
        {
            resource: "fresh_desk_form_configs",
            body: "types{id, name}, priorities{id, name}, recaptchaKeys{secretKey, siteKey}",
        },
    ].filter((q) => q);
};

FreshDeskFormRef.dataMapper = ["freshDeskFormConfigs", "data"];

export default FreshDeskFormRef;

function modalErrorBuilder(modalErrors, errorType, i18n) {
    return (modalErrors || []).map(({ message, type }) =>
        type === errorType ? (
            <div className={styles["error"]} key={message}>
                <small>{i18n`${message}`}</small>
            </div>
        ) : null,
    );
}
